import { Badge, Box, Button, HStack, Text, VStack } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import { type KanbanCard as KanbanCardType } from "../../services/sourcingApi"
import { titleizeAddress } from "../../utils/propertyUtils"

interface KanbanCardProps {
  card: KanbanCardType;
  onAcquiredRecovery?: (id: number) => void;
}

const getListingStatusBadge = (status: string) => {
  switch (status) {
    case "for_sale":
      return { colorPalette: "green", label: "For Sale" }
    case "under_offer":
      return { colorPalette: "orange", label: "Under Offer" }
    case "sold_stc":
      return { colorPalette: "red", label: "Sold STC" }
    case "sold":
      return { colorPalette: "red", label: "Sold" }
    default:
      return { colorPalette: "gray", label: "Unknown" }
  }
}

const getEpcBadge = (rating: string | null | undefined) => {
  if (!rating) return null
  const r = rating.toUpperCase()
  const colorPalette =
    r === "A" || r === "B" ? "green"
    : r === "C" ? "teal"
    : r === "D" ? "yellow"
    : r === "E" ? "orange"
    : "red"
  return { colorPalette, label: `EPC ${r}` }
}

const formatCurrency = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "N/A"
  const num = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(num)) return "N/A"
  return `£${num.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
}

const formatRoi = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "N/A"
  return `${value.toFixed(1)}%`
}

const formatPayback = (years: number | null | undefined): string => {
  if (years === null || years === undefined) return "N/A"
  return `${years.toFixed(1)} yrs`
}

const getRoiColor = (roi: number | null | undefined): string => {
  if (roi === null || roi === undefined) return "gray.500"
  if (roi >= 8) return "green.600"
  if (roi >= 4) return "orange.500"
  return "gray.500"
}

const KanbanCard = ({ card, onAcquiredRecovery }: KanbanCardProps) => {
  const navigate = useNavigate()
  const { scrapedProperty } = card
  const { home } = scrapedProperty
  const statusBadge = getListingStatusBadge(card.listingStatus)
  const epcBadge = getEpcBadge(scrapedProperty.epcRating)
  const isUnderOffer = card.listingStatus === "under_offer"
  const roiColor = getRoiColor(home.upgradesRoi)

  const propertyDetailLine =
    [
      scrapedProperty.bedrooms != null ? `${scrapedProperty.bedrooms} bed` : null,
      scrapedProperty.propertyType || null,
    ]
      .filter(Boolean)
      .join(" · ") || "Details unavailable"

  const daysOnMarket = scrapedProperty.daysOnMarket
  const isLongOnMarket = daysOnMarket != null && daysOnMarket >= 60

  const handleCardClick = () => {
    navigate(`/home/${home.id}?opportunityId=${card.id}`)
  }

  return (
    <Box
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      p={3}
      cursor="pointer"
      _hover={{ shadow: "md" }}
      w="100%"
      borderLeft={isUnderOffer ? "4px solid" : undefined}
      borderLeftColor={isUnderOffer ? "orange.400" : undefined}
      onClick={handleCardClick}
    >
      <VStack align="stretch" gap={2}>
        {/* Address */}
        <Text fontWeight="semibold" fontSize="sm" lineClamp={1} color="gray.900">
          {titleizeAddress(scrapedProperty.address)}
        </Text>

        {/* Property details + EPC */}
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" color="gray.500">
            {propertyDetailLine}
          </Text>
          {epcBadge && (
            <Badge colorPalette={epcBadge.colorPalette} size="sm" flexShrink={0}>
              {epcBadge.label}
            </Badge>
          )}
        </HStack>

        {/* Price */}
        <Text fontWeight="bold" fontSize="sm" color="gray.900">
          {formatCurrency(card.currentPrice || scrapedProperty.price)}
        </Text>

        {/* Key investor metrics */}
        <HStack gap={3}>
          <VStack gap={0} align="start">
            <Text fontSize="9px" color="gray.400" fontWeight="medium" letterSpacing="0.5px" textTransform="uppercase">
              ROI
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color={roiColor}>
              {formatRoi(home.upgradesRoi)}
            </Text>
          </VStack>
          <Box w="1px" h="28px" bg="gray.100" />
          <VStack gap={0} align="start">
            <Text fontSize="9px" color="gray.400" fontWeight="medium" letterSpacing="0.5px" textTransform="uppercase">
              Payback
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {formatPayback(home.upgradesPaybackPeriod)}
            </Text>
          </VStack>
          <Box w="1px" h="28px" bg="gray.100" />
          <VStack gap={0} align="start">
            <Text fontSize="9px" color="gray.400" fontWeight="medium" letterSpacing="0.5px" textTransform="uppercase">
              Works
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {formatCurrency(home.upgradesTotalCost)}
            </Text>
          </VStack>
        </HStack>

        {/* Days on market */}
        {daysOnMarket != null && (
          <Text fontSize="xs" color={isLongOnMarket ? "orange.500" : "gray.400"}>
            {daysOnMarket} days on market
          </Text>
        )}

        {/* Status badges */}
        <HStack gap={1} flexWrap="wrap">
          <Badge colorPalette={statusBadge.colorPalette} size="sm">
            {statusBadge.label}
          </Badge>
          {card.isStale && (
            <Badge colorPalette="gray" size="sm">Stale</Badge>
          )}
        </HStack>

        {/* Missed out recovery */}
        {onAcquiredRecovery && (
          <Button
            size="xs"
            variant="outline"
            colorPalette="green"
            onClick={(e) => {
              e.stopPropagation()
              onAcquiredRecovery(card.id)
            }}
          >
            I acquired this
          </Button>
        )}
      </VStack>
    </Box>
  )
}

export default KanbanCard
